import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import profile from "../images/profile.jpg";
import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { roleOf, ROLES, ROLE_LABELS } from "../auth/permissions";
import { setActiveBusinessId, clearActiveBusinessId } from "../auth/tokenStore";
import { brandFor } from "../businessBranding";
import { getBranding } from "../theme/brandingStore";
import CalendarMonth, {
  DAY_MS,
  startOfDay,
  shiftDays,
  shiftMonth,
  monthOf,
} from "../components/CalendarMonth";
import "../overview.css";

/* ------------------------------------------------------------------ *
 * Group overview — stats across every business in the group.
 * Standalone page: no app header, no side nav (see `avoidHeader` in
 * BasePage, src/components.js).
 * ------------------------------------------------------------------ */

/* ==================================================================== *
 * DATA — GET /group/stats?from=YYYY-MM-DD&to=YYYY-MM-DD
 *
 * Implemented server-side in src/controllers/GroupController.js
 * (Rays-Florist-Server). Response is
 *   { success, task: 'group', data: { range, group, businesses } }
 * with money as raw AUD numbers and every delta as `*_delta_pct`, or
 * null where there is no comparable prior window.
 *
 * SCOPING — this is the one screen outside a business, so the request
 *   carries no `business-id` header and the endpoint ignores the token's
 *   business_id claim for data. It lists the caller's memberships,
 *   filters them by `reports:read`, and queries each business inside its
 *   own tenant context.
 *
 * PHASE ONE — these come back null and the UI hides them rather than
 *   inventing a figure: `unpaid`, `net_profit`, `money.wages`, `tier`,
 *   `health` and `opened_year`. (`money.fd_commission` and
 *   `money.gross_profit` are live now: revenue − commission.) They need
 *   payment status, labour cost and a costed bill of materials, none of
 *   which exist in the schema yet. `production.stages` carries only
 *   `checked` and `open` — delivered and loaded have no backing field
 *   and are omitted rather than reported as zero.
 *
 * DEFINITIONS the endpoint commits to, so the numbers here can be read
 *   against other screens:
 *   - Revenue is line level, (customPrice ?? price) x amount, and will
 *     NOT match screens built on price_detail.total_price.
 *   - "In range" means delivery date, never creation date.
 *   - Orders counts delivered occurrences, so a weekly order delivered
 *     four times is four orders and avg_order_value stays revenue over
 *     orders on one basis.
 *   - production.total equals products_made, so the bar and the KPI
 *     agree.
 *
 * PRESENTATION stays here: colours, logos and the accent per business
 * are keyed off the response, never sent by the API.
 * ==================================================================== */

/* Preset pills. Trailing windows ending today, counted inclusively (so
   "30 days" is 25 Jul – 23 Aug). "Quarter" and "Year" are assumed to be
   trailing 90 / 365 days until the team confirms otherwise. */
const RANGES = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "Quarter", days: 90 },
  { label: "Year", days: 365 },
];

/* Per-business presentation, assigned by position in the response. The
   API sorts businesses by revenue descending, so the same business keeps
   the same colour between refreshes of an unchanged range. */
const PALETTE = [
  {
    accent: "#8f7136",
    line: "#e8c98a",
    fill: "#3a2f1c",
    accentClass: "is-accent",
    sellers: ["#8f7136", "#b79856", "#c9ac74", "#dcc79a"],
  },
  {
    accent: "#3f6f6a",
    line: "#7fae9f",
    fill: "#1f3330",
    accentClass: "is-accent-teal",
    sellers: ["#3f6f6a", "#5c8d86", "#8bb0aa", "#bcd4d0"],
  },
  {
    accent: "#7a5b7d",
    line: "#c8a6cb",
    fill: "#2f2431",
    accentClass: "is-accent",
    sellers: ["#7a5b7d", "#9a7d9d", "#b9a1bb", "#d6c6d8"],
  },
  {
    accent: "#6b6f3f",
    line: "#c2c67f",
    fill: "#2b2d1c",
    accentClass: "is-accent",
    sellers: ["#6b6f3f", "#8a8e5c", "#a9ad82", "#cbceac"],
  },
];

const paletteFor = (index) => PALETTE[index % PALETTE.length];

/* A business that has its own theme (Settings -> Branding) wears it here
   too; one still on the default palette takes a slot by position so the
   cards stay distinguishable. */
function paletteForBusiness(business, index) {
  const branding = getBranding(business);
  if (!branding.isOwn) return paletteFor(index);
  const t = branding.tokens;
  return {
    accent: t.brand,
    line: t["brand-bright"],
    fill: t["brand-night"],
    tint: t["brand-tint"],
    accentClass: "is-accent",
    sellers: [t.brand, t["brand-lite"], t["brand-soft"], t["brand-pale"]],
  };
}

/* Only `checked` and `open` come back today. Delivered and loaded are
   listed so the bar keeps working the moment the server can fill them. */
const STAGES = {
  delivered: { label: "Delivered", tone: "strong" },
  loaded: { label: "Loaded", tone: "mid" },
  checked: { label: "Checked", tone: "strong" },
  open: { label: "Open", tone: "rest" },
};

const stageColor = (palette, key) => {
  const tone = (STAGES[key] || {}).tone;
  if (tone === "strong") return palette.accent;
  if (tone === "mid") return palette.sellers[1];
  return "#cfc7b5";
};

const stageLabel = (key) => (STAGES[key] || {}).label || key;

/* Staff counts per business and role (section 4) come from
   GET /group/users — the same list the Manage page (/overview/users)
   shows, so the card and that page always agree. */

/* One colour per access level, shared by the stacked bar, its legend and
   the per-business matrix. Keyed by the role ids in auth/permissions. */
const ROLE_TONES = {
  owner: "#191714",
  admin: "#8f7136",
  staff: "#3f6f6a",
  driver: "#b79856",
};

/* "Owner · full access" -> "Owner". */
const roleShortLabel = (role) => (ROLE_LABELS[role] || role).split(" · ")[0];

/* ---------- formatting ----------
   The API sends raw numbers; `Num` animates whatever display string it
   is given, so all formatting happens here. */

/** 3200000 -> "$3.2M", 486200 -> "$486.2k", 392 -> "$392".
    Values that round up to the next tier step up too, so 999,950 reads
    "$1.0M" rather than "$1000.0k". */
function money(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value) || 0;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 999950) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 999.95) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs).toLocaleString("en-AU")}`;
}

/** 1284 -> "1,284". */
function count(value) {
  return (Number(value) || 0).toLocaleString("en-AU");
}

/** 11.4 -> "+11.4%", -2.3 -> "-2.3%", null -> null (the chip is hidden). */
function deltaLabel(pct) {
  if (pct === null || pct === undefined) return null;
  const n = Number(pct);
  if (!Number.isFinite(n)) return null;
  return `${n > 0 ? "+" : ""}${n}%`;
}

/* ---------- first-load skeleton ----------
   The glare is painted by `.is-loading .ov-skel::before/::after`, which
   size themselves against the block's real content — so the layout has to
   be on screen for there to be anything to shimmer. A refetch keeps the
   previous response mounted and glares over that; the very first load has
   nothing yet, so stand in a shape-only payload.

   Business names come from the session's memberships where possible, so
   the card count and headings are already right and only the figures
   resolve when the response lands. */
const BLANK_METRIC = { value: 0, delta_pct: null };

function placeholderStats(memberships, days) {
  const seats =
    memberships && memberships.length ? memberships : [null, null];

  const businesses = seats.map((m, i) => ({
    business_id: (m && String(m.business_id)) || `placeholder-${i}`,
    name: (m && m.business_name) || "\u2014",
    code: (m && m.business_code) || null,
    tier: null,
    staff_count: null,
    opened_year: null,
    health: null,
    kpis: {
      revenue: BLANK_METRIC,
      orders: BLANK_METRIC,
      avg_order_value: BLANK_METRIC,
      unpaid: null,
    },
    production: {
      total: 0,
      stages: [
        { key: "checked", count: 0 },
        { key: "open", count: 0 },
      ],
    },
    // Four rows so the block reserves the height the real list will need.
    top_sellers: {
      share_pct: 0,
      items: [0, 1, 2, 3].map((n) => ({ name: "\u2014", revenue: 0 })),
    },
    money: { gross_profit: null, fd_commission: null, net_profit: null, wages: null },
  }));

  return {
    range: { days },
    group: {
      revenue: 0,
      revenue_delta_pct: null,
      net_profit: null,
      kpis: {
        orders: BLANK_METRIC,
        products_made: BLANK_METRIC,
        avg_order_value: BLANK_METRIC,
        unpaid: null,
      },
      split: businesses.map((b) => ({
        business_id: b.business_id,
        revenue: 0,
        share_pct: 0,
      })),
      series: {
        labels: ["", "", "", ""],
        businesses: businesses.map((b) => ({
          business_id: b.business_id,
          points: [0, 0, 0, 0],
        })),
      },
    },
    businesses,
  };
}

/** A KPI tile, or null when the metric has no data behind it yet. */
function kpi(label, metric, format, sub) {
  if (!metric || metric.value === null || metric.value === undefined) return null;
  const delta = deltaLabel(metric.delta_pct);
  return {
    label,
    value: format(metric.value),
    delta,
    down: delta !== null && Number(metric.delta_pct) < 0,
    sub,
  };
}

/* Date helpers (startOfDay, shiftDays, …) live with the shared
   CalendarMonth component now. */

const MONTHS =["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* "25 Jul – 23 Aug 2026" — year once at the end, or on both if they differ.
   (Not Intl: en-AU renders "June"/"July" for the short month.) */
function formatRange(from, to) {
  const dm = (t) => {
    const d = new Date(t);
    return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  };
  const fromYear = new Date(from).getFullYear();
  const toYear = new Date(to).getFullYear();
  if (fromYear !== toYear) {
    return `${dm(from)} ${fromYear} – ${dm(to)} ${toYear}`;
  }
  return `${dm(from)} – ${dm(to)} ${toYear}`;
}

function formatInput(t) {
  return t ? new Date(t).toLocaleDateString("en-AU") : "";
}

/* Local calendar day as YYYY-MM-DD. Not toISOString(), which converts to
   UTC first and can hand the server the previous day. */
function isoDate(t) {
  const d = new Date(t);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* Inclusive day count: 25 Jul – 23 Aug is "30 days". */
function daysBetween(from, to) {
  return Math.round((to - from) / DAY_MS) + 1;
}

/* ---------- load animation ---------- */

const ANIM_MS = 1100;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/* Eased 0 → 1 progress, replayed whenever `key` changes (e.g. a new date
   range). Jumps straight to 1 for users who prefer reduced motion. */
function useLoadProgress(key) {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (key === null) {
      setP(0); // still loading — hold everything at zero
      return undefined;
    }
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setP(1);
      return undefined;
    }
    let frame;
    let start = null; // clock starts on the first painted frame (rAF pauses in hidden tabs)
    setP(0);
    const step = (now) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / ANIM_MS);
      setP(easeOutCubic(t));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [key]);
  return p;
}

/* Renders a display string like "$486.2k", "1,284" or "+11.4%" counting up
   from zero — keeps the prefix/suffix, decimals and thousands separator. */
function Num({ v, p }) {
  const m = /^([^\d]*)([\d,]+(?:\.\d+)?)(.*)$/.exec(String(v));
  if (!m || p >= 1) return v;
  const [, prefix, digits, suffix] = m;
  const target = parseFloat(digits.replace(/,/g, ""));
  const decimals = (digits.split(".")[1] || "").length;
  const grouped = digits.indexOf(",") !== -1;
  const n = target * p;
  const text = grouped
    ? n.toLocaleString("en-AU", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : n.toFixed(decimals);
  return `${prefix}${text}${suffix}`;
}

/* ---------- chart / donut helpers ---------- */

/* Points rise from the baseline into place as `p` goes 0 → 1.
   A short range can span a single ISO week, so guard the divisor and draw
   that lone point as a flat line rather than dividing by zero. */
function toPoints(values, max, width, height, p = 1) {
  const series = values.length === 1 ? [values[0], values[0]] : values;
  const span = Math.max(1, series.length - 1);
  return series
    .map((v, i) => {
      const x = (i / span) * width;
      const y = height - ((v * p) / Math.max(1, max)) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

const DONUT_TRACK = "#eee9df";

function withShares(sellers, p = 1) {
  // A range with no sales still renders the donut, as an empty track.
  const total = sellers.reduce((sum, s) => sum + s.amount, 0);
  if (total <= 0) {
    return {
      gradient: `conic-gradient(${DONUT_TRACK} 0% 100%)`,
      rows: sellers.map((s) => ({
        name: s.name,
        color: s.color,
        value: money(s.amount),
        share: "0%",
      })),
    };
  }

  let acc = 0;
  const stops = sellers.map((s) => {
    const from = (acc / total) * 100 * p;
    acc += s.amount;
    const to = (acc / total) * 100 * p;
    return `${s.color} ${from.toFixed(2)}% ${to.toFixed(2)}%`;
  });
  if (p < 1) stops.push(`${DONUT_TRACK} ${(100 * p).toFixed(2)}% 100%`);
  return {
    gradient: `conic-gradient(from -90deg, ${stops.join(",")})`,
    rows: sellers.map((s) => ({
      name: s.name,
      color: s.color,
      value: money(s.amount),
      share: `${Math.round((s.amount / total) * 100)}%`,
    })),
  };
}

/* ---------- page ---------- */

export function OverviewPage() {
  const today = useMemo(() => startOfDay(Date.now()), []);

  // Applied range — what the page (and, later, the fetch) is scoped to.
  const [range, setRange] = useState("30 days");
  const [from, setFrom] = useState(() => shiftDays(today, -(30 - 1)));
  const [to, setTo] = useState(today);

  // Popover state — a draft that only lands on Apply.
  const [calOpen, setCalOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [viewMonth, setViewMonth] = useState(() => monthOf(to)); // right-hand month
  const pickerRef = useRef(null);

  // Profile menu (top-right).
  const auth = useAuth() || {};
  const user = auth.user || {};
  const displayName = user.full_name || user.userName || "Ray Milano";
  const displayRole = roleOf(user) || "No access";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const navigate = useNavigate();

  // Being on the overview means being outside any business — landing here
  // (including via the sidebar "Exit" button) drops the active business id,
  // so API calls stop carrying the `business-id` header.
  useEffect(() => {
    clearActiveBusinessId();
  }, []);

  function openBusiness(biz) {
    setActiveBusinessId(biz.business_id);
    navigate("/");
  }

  const rangeLabel = useMemo(() => formatRange(from, to), [from, to]);
  const rangeDays = useMemo(() => `${daysBetween(from, to)} days`, [from, to]);
  const draftDays =
    draftFrom && draftTo ? `${daysBetween(draftFrom, draftTo)} days` : rangeDays;

  function openCal() {
    setDraftFrom(from);
    setDraftTo(to);
    setViewMonth(monthOf(to));
    setMenuOpen(false);
    setCalOpen(true);
  }

  function closeCal() {
    // Cancel / × / outside click: discard the draft.
    setCalOpen(false);
  }

  function applyCal() {
    if (!draftFrom || !draftTo) return;
    setFrom(draftFrom);
    setTo(draftTo);
    const preset = RANGES.find(
      (r) => draftTo === today && shiftDays(today, -(r.days - 1)) === draftFrom
    );
    setRange(preset ? preset.label : null);
    setCalOpen(false);
    // The fetch effect keys off from/to, so setting them refetches.
  }

  function pickPreset(preset) {
    setRange(preset.label);
    setTo(today);
    setFrom(shiftDays(today, -(preset.days - 1)));
    setCalOpen(false);
  }

  function pickDate(stamp) {
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(stamp);
      setDraftTo(null);
    } else if (stamp < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(stamp);
    } else {
      setDraftTo(stamp);
    }
  }

  // Close the calendar / profile popovers on outside click or Escape.
  useEffect(() => {
    if (!calOpen && !menuOpen) return undefined;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setCalOpen(false);
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setCalOpen(false);
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [calOpen, menuOpen]);

  async function handleLogout() {
    setMenuOpen(false);
    if (auth.logout) await auth.logout(); // ProtectedRoute redirects to /login once user is null
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  /* Catalogue and funeral-director counts for section 4 — not
     range-scoped, so fetched once each.
     GET /group/products?summary=1 → { businesses: [{ business_id, name,
       products: { total, shared } }], total, shared_total }
     GET /group/fds?summary=1 → same shape with `fds` per business. */
  const [catalogue, setCatalogue] = useState(null);
  const [fdBook, setFdBook] = useState(null);
  // GET /group/users → { businesses: [{ business_id, name, code }],
  //   users: [{ memberships: [{ business_id, role }] }] }
  const [staffBook, setStaffBook] = useState(null);
  useEffect(() => {
    let live = true;
    const empty = { businesses: [], total: 0, shared_total: 0 };
    const fetchSummary = (path, set) =>
      axios
        .get(`${BASE_SERVER_URL}${path}`, { params: { summary: 1 } })
        .then((res) => {
          if (!live) return;
          const data = res && res.data && res.data.data;
          set(data || empty);
        })
        .catch(() => {
          if (live) set(empty);
        });
    fetchSummary("/group/products", setCatalogue);
    fetchSummary("/group/fds", setFdBook);
    axios
      .get(`${BASE_SERVER_URL}/group/users`)
      .then((res) => {
        if (!live) return;
        const data = res && res.data && res.data.data;
        setStaffBook(data || { businesses: [], users: [] });
      })
      .catch(() => {
        if (live) setStaffBook({ businesses: [], users: [] });
      });
    return () => {
      live = false;
    };
  }, []);

  /* One request per applied range. A slow reply for an abandoned range must
     not overwrite a newer one, so late responses are dropped on cleanup. */
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);

    axios
      .get(`${BASE_SERVER_URL}/group/stats`, {
        params: { from: isoDate(from), to: isoDate(to) },
      })
      .then((res) => {
        if (!live) return;
        const data = res && res.data && res.data.data;
        if (!data) throw new Error("Malformed response");
        setStats(data);
      })
      .catch((err) => {
        if (!live) return;
        setStats(null);
        setError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "Could not load group stats."
        );
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [from, to]);

  const sk = (cls) => (loading ? `${cls} ov-skel` : cls);

  /* What the page renders from. A refetch keeps the previous response on
     screen and glares over it; the first load has none, so fall back to
     the shape-only stand-in. An error clears both — the error panel shows
     instead of a skeleton that would never resolve. */
  const view = useMemo(() => {
    if (stats) return stats;
    if (error) return null;
    return placeholderStats(auth.memberships, daysBetween(from, to));
  }, [stats, error, auth.memberships, from, to]);

  // Load animation — replays when the applied range changes.
  const p = useLoadProgress(loading || !stats ? null : `${from}-${to}`);

  /* ---- view models ---------------------------------------------------
     Presentation is joined onto the response here: the API sends ids and
     numbers, this assigns the colour, logo and display strings. */

  const businesses = useMemo(() => {
    if (!view) return [];
    return (view.businesses || []).map((b, i) => {
      const palette = paletteForBusiness(b, i);
      const mark = brandFor(b);
      const metaParts = [
        b.tier,
        b.staff_count ? `${b.staff_count} staff` : null,
        b.opened_year ? `opened ${b.opened_year}` : null,
      ].filter(Boolean);

      const kpis = [
        kpi("Revenue", b.kpis && b.kpis.revenue, money),
        kpi("Orders", b.kpis && b.kpis.orders, count),
        kpi("AOV", b.kpis && b.kpis.avg_order_value, money),
        kpi("Unpaid", b.kpis && b.kpis.unpaid, money),
      ].filter(Boolean);

      const production = b.production || { total: 0, stages: [] };
      const checked = (production.stages || []).find((s) => s.key === "checked");
      const money_ = b.money || {};
      const moneyCells = [
        { label: "Gross profit", value: money(money_.gross_profit) },
        { label: "Commission", value: money(money_.fd_commission), soft: true },
        { label: "Net profit", value: money(money_.net_profit) },
        { label: "Wages", value: money(money_.wages), soft: true },
      ].filter((cell) => cell.value !== null);

      return {
        business_id: b.business_id,
        name: b.name,
        meta: metaParts.join(" · "),
        logo: mark.logo,
        logoHeight: mark.logoHeight,
        monogram: mark.monogram,
        color: palette.accent,
        accentClass: palette.accentClass,
        accentTint: palette.tint || null,
        line: palette.line,
        fill: palette.fill,
        health: b.health,
        kpis,
        production: {
          total: production.total || 0,
          // Every segment would be flex-grow:0 and the bar would collapse to
          // nothing, reading as broken rather than empty.
          isEmpty: !(production.total > 0),
          // "% delivered" needs a stage that does not exist yet; report the
          // share that has actually been checked instead.
          checkedPct:
            production.total > 0 && checked
              ? Math.round((checked.count / production.total) * 100)
              : null,
          stages: (production.stages || []).map((s) => ({
            key: s.key,
            label: stageLabel(s.key),
            count: s.count,
            color: stageColor(palette, s.key),
          })),
        },
        sellersShare:
          b.top_sellers && b.top_sellers.share_pct !== null
            ? `${b.top_sellers.share_pct}%`
            : null,
        sellers: ((b.top_sellers && b.top_sellers.items) || []).map((item, j) => ({
          name: item.name,
          amount: item.revenue,
          color: palette.sellers[j % palette.sellers.length],
        })),
        money: moneyCells,
      };
    });
  }, [view]);

  const group = useMemo(() => {
    if (!view) return null;
    const g = view.group || {};
    const byId = new Map(businesses.map((b) => [b.business_id, b]));

    return {
      revenue: money(g.revenue),
      revenueDelta: deltaLabel(g.revenue_delta_pct),
      netNote: g.net_profit !== null ? `Net ${money(g.net_profit)}` : null,
      commissionNote:
        g.fd_commission !== null && g.fd_commission !== undefined
          ? `FD commission ${money(g.fd_commission)}`
          : null,
      kpis: [
        kpi("Orders", g.kpis && g.kpis.orders, count, "all businesses"),
        kpi(
          "Products made",
          g.kpis && g.kpis.products_made,
          count,
          `across ${(view.range && view.range.days) || daysBetween(from, to)} days`
        ),
        kpi("Avg order value", g.kpis && g.kpis.avg_order_value, money, "group average"),
        kpi("Unpaid", g.kpis && g.kpis.unpaid, money),
      ].filter(Boolean),
      split: (g.split || []).map((s) => {
        const biz = byId.get(s.business_id);
        return {
          name: (biz && biz.name) || "Unknown",
          value: money(s.revenue),
          share: s.share_pct,
          color: (biz && biz.color) || "#cfc7b5",
        };
      }),
      series: {
        labels: (g.series && g.series.labels) || [],
        lines: ((g.series && g.series.businesses) || []).map((s) => {
          const biz = byId.get(s.business_id);
          const index = businesses.findIndex((b) => b.business_id === s.business_id);
          const palette = biz || paletteFor(index < 0 ? 0 : index);
          return {
            business_id: s.business_id,
            name: (biz && biz.name) || "Unknown",
            points: s.points || [],
            color: palette.line,
            fill: palette.fill,
          };
        }),
      },
    };
  }, [view, businesses, from, to]);

  /* Section 4 — staff, products and funeral directors, all live.
     Businesses that also appear in the stats response borrow that card's
     colour so swatches agree across sections; the rest take the next
     palette slot. */
  const directory = useMemo(() => {
    // One membership = one seat, so someone in two businesses counts in
    // each. Legacy role strings are normalised through roleOf.
    /* Until a card's request lands it renders these stand-in rows (named
       from the session's memberships where possible), so the glare has
       the real layout to sweep over — same idea as placeholderStats. */
    const seats = (
      auth.memberships && auth.memberships.length ? auth.memberships : [null, null]
    ).map((m, i) => ({
      business_id: (m && String(m.business_id)) || `placeholder-${i}`,
      name: (m && m.business_name) || "\u2014",
    }));

    const staffUsers = (staffBook && staffBook.users) || [];
    const staffBusinesses = staffBook ? staffBook.businesses || [] : seats;
    const list = staffBusinesses.map((b, i) => {
      const id = String(b.business_id);
      const live = businesses.find((x) => String(x.business_id) === id);
      const staff = {};
      staffUsers.forEach((u) => {
        (u.memberships || []).forEach((m) => {
          if (String(m.business_id) !== id) return;
          const role = roleOf(m) || "staff";
          staff[role] = (staff[role] || 0) + 1;
        });
      });
      const staffTotal = ROLES.reduce((n, r) => n + (staff[r] || 0), 0);
      return {
        business_id: id,
        name: b.name,
        color: (live && live.color) || paletteFor(i).accent,
        staff,
        staffTotal,
      };
    });

    const staffTotal = list.reduce((n, b) => n + b.staffTotal, 0);

    // Products and FDs: one row per business the caller can read them in.
    // `total` is distinct rows across the group, so a global one counts
    // once, under the business that created it.
    const sharedRows = (summary, key) => {
      const total = summary ? summary.total || 0 : 0;
      return (summary ? summary.businesses || [] : seats).map((b, i) => {
        const live = businesses.find((x) => String(x.business_id) === String(b.business_id));
        const owned = (b[key] && b[key].total) || 0;
        const shared = (b[key] && b[key].shared) || 0;
        return {
          business_id: String(b.business_id),
          name: b.name,
          color: (live && live.color) || paletteFor(i).accent,
          total: owned,
          shared,
          share: total > 0 ? Math.round((owned / total) * 100) : 0,
        };
      });
    };

    const byRole = ROLES.map((role) => {
      const total = list.reduce((n, b) => n + (b.staff[role] || 0), 0);
      return {
        key: role,
        label: roleShortLabel(role),
        color: ROLE_TONES[role] || "#cfc7b5",
        count: total,
      };
    });

    return {
      businesses: list,
      staffTotal,
      byRole,
      catalogue: sharedRows(catalogue, "products"),
      productsTotal: catalogue ? catalogue.total || 0 : 0,
      globalTotal: catalogue ? catalogue.shared_total || 0 : 0,
      fds: sharedRows(fdBook, "fds"),
      fdsTotal: fdBook ? fdBook.total || 0 : 0,
      fdsGlobal: fdBook ? fdBook.shared_total || 0 : 0,
    };
  }, [businesses, catalogue, fdBook, staffBook, auth.memberships]);

  // Each card counts up when its own request lands.
  const pd = useLoadProgress(staffBook ? "staff" : null);
  /* Section 4 cards load independently of /group/stats, so each carries
     its own `is-loading` and only marks its blocks while it is waiting —
     a stats refetch must not glare over cards that already have data. */
  const staffLoading = !staffBook;
  const catalogueLoading = !catalogue;
  const fdsLoading = !fdBook;
  const dirCard = (isLoading) => `ov-dir-card${isLoading ? " is-loading" : ""}`;
  const dirSk = (isLoading, cls) => (isLoading ? `${cls} ov-skel` : cls);
  const pc = useLoadProgress(catalogue ? "catalogue" : null);
  const pf = useLoadProgress(fdBook ? "fds" : null);

  const W = 760;
  const H = 132;
  const seriesMax = group
    ? Math.max(1, ...group.series.lines.flatMap((l) => l.points))
    : 1;

  const leftMonth = shiftMonth(viewMonth, -1);

  return (
    <div className={`overview-page${loading ? " is-loading" : ""}`}>
      {/* ---------- top bar ---------- */}
      <div className="ov-topbar">
        <span className="ov-spacer" />

        <div className="ov-datepicker" ref={pickerRef}>
          <div className="ov-segmented">
            {RANGES.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={range === preset.label ? "is-active" : ""}
                onClick={() => pickPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            className={`ov-range-btn${calOpen ? " is-open" : ""}`}
            onClick={() => (calOpen ? closeCal() : openCal())}
            aria-expanded={calOpen}
          >
            <span className="ov-range-label ov-num">{rangeLabel}</span>
            <span className="ov-range-days">{rangeDays}</span>
            <span className="ov-caret">▾</span>
          </button>

          {calOpen && (
            <div className="ov-cal">
              <div className="ov-cal-head">
                <span>Custom range</span>
                <button type="button" className="ov-cal-close" onClick={closeCal} aria-label="Close">
                  ×
                </button>
              </div>

              <div className="ov-cal-inputs">
                <div className="ov-field">
                  <label className="ov-label">From</label>
                  <input readOnly value={formatInput(draftFrom)} />
                </div>
                <div className="ov-field">
                  <label className="ov-label">To</label>
                  <input readOnly value={formatInput(draftTo)} />
                </div>
              </div>

              <div className="ov-cal-months">
                <CalendarMonth
                  year={leftMonth.year}
                  monthIndex={leftMonth.monthIndex}
                  from={draftFrom}
                  to={draftTo}
                  onPick={pickDate}
                  onPrev={() => setViewMonth((m) => shiftMonth(m, -1))}
                />
                <CalendarMonth
                  year={viewMonth.year}
                  monthIndex={viewMonth.monthIndex}
                  from={draftFrom}
                  to={draftTo}
                  onPick={pickDate}
                  onNext={() => setViewMonth((m) => shiftMonth(m, 1))}
                />
              </div>

              <div className="ov-cal-foot">
                <span>Compared with previous {draftDays}</span>
                <button type="button" className="ov-btn ov-btn-ghost" onClick={closeCal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ov-btn ov-btn-primary"
                  onClick={applyCal}
                  disabled={!draftFrom || !draftTo}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <span className="ov-rule-v" />

        <div className="ov-user-wrap" ref={menuRef}>
          <button
            type="button"
            className={`ov-user${menuOpen ? " is-open" : ""}`}
            onClick={() => {
              setCalOpen(false);
              setMenuOpen((prev) => !prev);
            }}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <img src={profile} alt="" />
            <div>
              <div className="ov-user-name">{displayName}</div>
              <div className="ov-user-role">{displayRole}</div>
            </div>
            <span className="ov-caret">▾</span>
          </button>

          {menuOpen && (
            <div className="ov-profile-menu" role="menu">
              <div className="ov-profile-card">
                <img src={profile} alt="" />
                <div className="ov-profile-text">
                  <div className="ov-profile-name">{displayName}</div>
                  <div className="ov-profile-role">{displayRole}</div>
                  {user.userName && (
                    <div className="ov-profile-user">@{user.userName}</div>
                  )}
                </div>
              </div>
              <div className="ov-profile-rows">
                <div className="ov-profile-row">
                  <span className="ov-label">Signed in as</span>
                  <span className="ov-num">{user.userName || displayName}</span>
                </div>
                <div className="ov-profile-row">
                  <span className="ov-label">Access</span>
                  <span>{displayRole}</span>
                </div>
              </div>
              <div className="ov-profile-foot">
                <button
                  type="button"
                  className="ov-btn ov-btn-ghost ov-btn-logout"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="ov-body">
        {error && (
          <div className="ov-panel">
            <div className="ov-panel-head">
              <b>Group stats unavailable</b>
            </div>
            <div className="ov-stack-legend">
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* ---------- section 1: summary overview ---------- */}
        {group && (
        <div>
          <div className="ov-section-head">
            <span className="ov-eyebrow">Summary Overview</span>
            <span className="ov-section-rule" />
            <span className="ov-section-meta">{rangeLabel}</span>
          </div>

          <div className="ov-group">
            <div className="ov-group-left">
              <div className={sk("ov-group-hero")}>
              <div className="ov-group-eyebrow">Group revenue</div>
              <div className="ov-group-total">
                <span className="ov-serif ov-figure ov-num">
                  <Num v={group.revenue} p={p} />
                </span>
                {group.revenueDelta && (
                  <span className="ov-delta-chip ov-fade" style={{ opacity: p }}>
                    {group.revenueDelta}
                  </span>
                )}
              </div>
              {group.netNote && <div className="ov-group-sub">{group.netNote}</div>}
              {group.commissionNote && (
                <div className="ov-group-sub">{group.commissionNote}</div>
              )}
              </div>

              <div className={sk("ov-split")}>
                {group.split.map((b) => (
                  <div key={b.name}>
                    <div className="ov-split-row">
                      <span className="ov-split-name">
                        <span className="ov-swatch" style={{ background: b.color }} />
                        {b.name}
                      </span>
                      <span className="ov-split-value ov-num">
                        <Num v={b.value} p={p} /> · <Num v={`${b.share}%`} p={p} />
                      </span>
                    </div>
                    <div className="ov-track-dark">
                      <span style={{ width: `${b.share * p}%`, background: b.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ov-group-divider" />

            <div className="ov-group-right">
              <div
                className="ov-kpi-row"
                style={{ gridTemplateColumns: `repeat(${group.kpis.length}, 1fr)` }}
              >
                {group.kpis.map((k) => (
                  <div key={k.label} className={sk("ov-kpi")}>
                    <div className="ov-kpi-label">{k.label}</div>
                    <div className="ov-kpi-value">
                      <span className="ov-serif ov-figure ov-num">
                        <Num v={k.value} p={p} />
                      </span>
                      <span
                        className={`ov-kpi-delta ${k.down ? "ov-down" : "ov-up"}`}
                        style={{ opacity: p }}
                      >
                        {k.delta}
                      </span>
                    </div>
                    {k.sub && <div className="ov-kpi-sub">{k.sub}</div>}
                  </div>
                ))}
              </div>

              <div className={sk("ov-chart-block")}>
              <div className="ov-chart-head">
                <span>Revenue by week</span>
                <span className="ov-spacer" />
                {group.series.lines.map((l) => (
                  <span key={l.business_id} className="ov-legend">
                    <i style={{ background: l.color }} />
                    {l.name}
                  </span>
                ))}
              </div>

              <div className="ov-chart">
                <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                  {[0, 1, 2].map((i) => (
                    <line
                      key={i}
                      x1="0"
                      x2={W}
                      y1={(i * H) / 3}
                      y2={(i * H) / 3}
                      stroke="#2b261e"
                      strokeWidth="1"
                    />
                  ))}
                  {/* Only the leading business is filled — stacking translucent
                      fills for every line turns the chart to mud. */}
                  {group.series.lines.slice(0, 1).map((l) => (
                    <polygon
                      key={`fill-${l.business_id}`}
                      points={`0,${H} ${toPoints(l.points, seriesMax, W, H, p)} ${W},${H}`}
                      fill={l.fill}
                      opacity={0.7 * p}
                    />
                  ))}
                  {group.series.lines.map((l) => (
                    <polyline
                      key={l.business_id}
                      points={toPoints(l.points, seriesMax, W, H, p)}
                      fill="none"
                      stroke={l.color}
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </svg>
              </div>

              <div className="ov-chart-axis">
                {group.series.labels.map((l, i) => (
                  <span key={`${l}-${i}`}>{l}</span>
                ))}
              </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ---------- sections 2 & 3: per business ---------- */}
        {businesses.length > 0 && (
        <div>
          <div className="ov-section-head">
            <span className="ov-eyebrow">By business</span>
            <span className="ov-section-rule" />
          </div>

          <div
            className="ov-businesses"
            style={businesses.length === 1 ? { gridTemplateColumns: "1fr" } : undefined}
          >
            {businesses.map((biz) => {
              const donut = withShares(biz.sellers, p);
              return (
                <div key={biz.business_id} className="ov-biz">
                  <div className="ov-biz-head">
                    <span className="ov-biz-accent" style={{ background: biz.color }} />
                    {biz.logo ? (
                      <img
                        className="ov-biz-logo"
                        src={biz.logo}
                        alt=""
                        style={biz.logoHeight ? { height: biz.logoHeight } : undefined}
                      />
                    ) : (
                      <span className="ov-biz-mark" style={{ color: biz.color }}>
                        {biz.monogram}
                      </span>
                    )}
                    <div className="ov-biz-title">
                      <div className="ov-serif ov-biz-name">{biz.name}</div>
                      {biz.meta && <div className="ov-biz-meta">{biz.meta}</div>}
                    </div>
                    {biz.health && (
                      <span
                        className={sk(
                          `ov-chip ${biz.health.status === "ok" ? "ov-chip-ok" : "ov-chip-warn"}`
                        )}
                      >
                        {biz.health.label}
                      </span>
                    )}
                    <button type="button" className="ov-link" onClick={() => openBusiness(biz)}>
                      Open
                    </button>
                  </div>

                  <div className="ov-biz-body">
                    <div
                      className="ov-biz-kpis"
                      style={{ gridTemplateColumns: `repeat(${biz.kpis.length}, 1fr)` }}
                    >
                      {biz.kpis.map((k) => (
                        <div key={k.label} className={sk("ov-biz-kpi")}>
                          <div className="ov-kpi-label">{k.label}</div>
                          <div className="ov-kpi-value">
                            <span className="ov-serif ov-figure ov-num">
                              <Num v={k.value} p={p} />
                            </span>
                            <span
                              className={`ov-kpi-delta ${k.down ? "ov-down" : "ov-up"}`}
                              style={{ opacity: p }}
                            >
                              {k.delta}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className={sk("ov-panel")}>
                      <div className="ov-panel-head">
                        <b>Production</b>
                        <span>
                          <Num v={biz.production.total.toLocaleString()} p={p} /> products
                          {biz.production.checkedPct !== null && (
                            <>
                              {" · "}
                              <Num v={`${biz.production.checkedPct}%`} p={p} /> checked
                            </>
                          )}
                        </span>
                      </div>
                      <div className="ov-stack" style={{ width: `${100 * p}%` }}>
                        {biz.production.isEmpty ? (
                          <i style={{ flexGrow: 1, background: DONUT_TRACK }} />
                        ) : (
                          biz.production.stages.map((s) => (
                            <i key={s.key} style={{ flexGrow: s.count, background: s.color }} />
                          ))
                        )}
                      </div>
                      <div className="ov-stack-legend">
                        {biz.production.stages.map((s) => (
                          <span key={s.key}>
                            <span className="ov-swatch-sm" style={{ background: s.color }} />
                            {s.label}{" "}
                            <b>
                              <Num v={s.count.toLocaleString()} p={p} />
                            </b>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className={sk("ov-sellers-block")}>
                      <div className="ov-block-head">
                        <b>Top sellers</b>
                        {biz.sellersShare && (
                          <span>
                            <Num v={biz.sellersShare} p={p} /> of revenue
                          </span>
                        )}
                      </div>
                      <div className="ov-sellers">
                        <div className="ov-donut" style={{ background: donut.gradient }}>
                          <div className="ov-donut-hole" />
                        </div>
                        <div className="ov-seller-list">
                          {donut.rows.length === 0 && (
                            <div className="ov-stack-legend">
                              <span>No sales in this range</span>
                            </div>
                          )}
                          {donut.rows.map((s) => (
                            <div key={s.name} className="ov-seller">
                              <span className="ov-swatch-sm" style={{ background: s.color }} />
                              <span className="ov-seller-name">{s.name}</span>
                              <span className="ov-seller-share">
                                <Num v={s.share} p={p} />
                              </span>
                              <span className="ov-seller-value">
                                <Num v={s.value} p={p} />
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Wages and profit need cost data the schema does not
                        carry yet, so the whole row stays out rather than
                        showing three blanks. */}
                    {biz.money.length > 0 && (
                      <div className="ov-money">
                        {biz.money.map((m, i) => (
                          <div
                            key={m.label}
                            className={sk(`ov-money-cell${i === 0 ? ` ${biz.accentClass}` : ""}`)}
                            style={i === 0 && biz.accentTint ? { background: biz.accentTint } : undefined}
                          >
                            <div className="ov-money-label">{m.label}</div>
                            <div className={`ov-money-value${m.soft ? " is-soft" : ""}`}>
                              <Num v={m.value} p={p} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* ---------- section 4: staff, products & funeral directors ---------- */}
        <div>
          <div className="ov-section-head">
            <span className="ov-eyebrow">People &amp; catalogue</span>
            <span className="ov-section-rule" />
          </div>

          <div className="ov-dir">
            {/* Staff — total, split by access level, then level × business */}
            <div className={dirCard(staffLoading)}>
              <div className="ov-dir-head">
                <div>
                  <div className="ov-kpi-label">Staff</div>
                  <div className={dirSk(staffLoading, "ov-kpi-value")}>
                    <span className="ov-serif ov-figure ov-num">
                      <Num v={count(directory.staffTotal)} p={pd} />
                    </span>
                    <span className="ov-kpi-sub">
                      across {directory.businesses.length} businesses
                    </span>
                  </div>
                </div>
                <button type="button" className="ov-link" onClick={() => navigate("/overview/users")}>
                  Manage
                </button>
              </div>

              <div className="ov-dir-body">
                <div>
                  <div className="ov-block-head">
                    <b>By access level</b>
                    <span>{directory.byRole.filter((r) => r.count > 0).length} levels in use</span>
                  </div>
                  <div className={dirSk(staffLoading, "ov-dir-skel-bar")}>
                  <div className="ov-stack" style={{ width: `${100 * pd}%` }}>
                    {directory.byRole
                      .filter((r) => r.count > 0)
                      .map((r) => (
                        <i key={r.key} style={{ flexGrow: r.count, background: r.color }} />
                      ))}
                  </div>
                  </div>
                  <div className={dirSk(staffLoading, "ov-stack-legend")}>
                    {directory.byRole.map((r) => (
                      <span key={r.key}>
                        <span className="ov-swatch-sm" style={{ background: r.color }} />
                        {r.label} <b><Num v={count(r.count)} p={pd} /></b>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="ov-block-head">
                    <b>By business</b>
                    <span>people per level</span>
                  </div>
                  <div className={dirSk(staffLoading, "ov-matrix-wrap")}>
                  <table className="ov-matrix">
                    <thead>
                      <tr>
                        <th>Business</th>
                        {directory.byRole.map((r) => (
                          <th key={r.key}>{r.label}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {directory.businesses.length === 0 && (
                        <tr>
                          <td colSpan={directory.byRole.length + 2} className="is-zero">
                            {staffBook ? "No staff yet." : "Loading staff…"}
                          </td>
                        </tr>
                      )}
                      {directory.businesses.map((b) => (
                        <tr key={b.business_id}>
                          <td>
                            <span className="ov-matrix-name">
                              <span className="ov-swatch-sm" style={{ background: b.color }} />
                              {b.name}
                            </span>
                          </td>
                          {directory.byRole.map((r) => {
                            const n = b.staff[r.key] || 0;
                            return (
                              <td key={r.key} className={n === 0 ? "is-zero" : undefined}>
                                {n === 0 ? "\u2014" : <Num v={count(n)} p={pd} />}
                              </td>
                            );
                          })}
                          <td className="is-total">
                            <Num v={count(b.staffTotal)} p={pd} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>All businesses</td>
                        {directory.byRole.map((r) => (
                          <td key={r.key}>
                            <Num v={count(r.count)} p={pd} />
                          </td>
                        ))}
                        <td className="is-total">
                          <Num v={count(directory.staffTotal)} p={pd} />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: products, then funeral directors. Each card
                shows the group total and the global count, then count and
                share per business. "Global" rows are shared with every
                business; each is counted once, under its creator. */}
            <div className="ov-dir-stack">
            <div className={dirCard(catalogueLoading)}>
              <div className="ov-dir-head">
                <div className="ov-dir-figures">
                  <div>
                    <div className="ov-kpi-label">Products</div>
                    <div className={dirSk(catalogueLoading, "ov-kpi-value")}>
                      <span className="ov-serif ov-figure ov-num">
                        <Num v={count(directory.productsTotal)} p={pc} />
                      </span>
                      <span className="ov-kpi-sub">in the catalogue</span>
                    </div>
                  </div>
                  <div>
                    <div className="ov-kpi-label">Global</div>
                    <div className={dirSk(catalogueLoading, "ov-kpi-value")}>
                      <span className="ov-serif ov-figure ov-num">
                        <Num v={count(directory.globalTotal)} p={pc} />
                      </span>
                      <span className="ov-kpi-sub">shared by all</span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="ov-link"
                  onClick={() => navigate("/overview/products")}
                >
                  Manage
                </button>
              </div>

              <div className="ov-dir-body">
                <div>
                  <div className="ov-block-head">
                    <b>By business</b>
                    <span>share of catalogue</span>
                  </div>
                  <div className="ov-dir-rows">
                    {directory.catalogue.length === 0 && (
                      <div className="ov-dir-sub">
                        {catalogue ? "No products yet." : "Loading catalogue…"}
                      </div>
                    )}
                    {directory.catalogue.map((b) => (
                      <div key={b.business_id} className={dirSk(catalogueLoading, "ov-dir-row")}>
                        <div className="ov-split-row">
                          <span className="ov-split-name">
                            <span className="ov-swatch" style={{ background: b.color }} />
                            {b.name}
                          </span>
                          <span className="ov-split-value ov-num">
                            <Num v={count(b.total)} p={pc} /> ·{" "}
                            <Num v={`${b.share}%`} p={pc} />
                          </span>
                        </div>
                        <div className="ov-track">
                          <span style={{ width: `${b.share * pc}%`, background: b.color }} />
                        </div>
                        <div className="ov-dir-sub">
                          <Num v={count(b.total - b.shared)} p={pc} /> private ·{" "}
                          <Num v={count(b.shared)} p={pc} /> global
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Funeral directors */}
            <div className={dirCard(fdsLoading)}>
              <div className="ov-dir-head">
                <div className="ov-dir-figures">
                  <div>
                    <div className="ov-kpi-label">Funeral directors</div>
                    <div className={dirSk(fdsLoading, "ov-kpi-value")}>
                      <span className="ov-serif ov-figure ov-num">
                        <Num v={count(directory.fdsTotal)} p={pf} />
                      </span>
                      <span className="ov-kpi-sub">across the group</span>
                    </div>
                  </div>
                  <div>
                    <div className="ov-kpi-label">Global</div>
                    <div className={dirSk(fdsLoading, "ov-kpi-value")}>
                      <span className="ov-serif ov-figure ov-num">
                        <Num v={count(directory.fdsGlobal)} p={pf} />
                      </span>
                      <span className="ov-kpi-sub">shared by all</span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="ov-link"
                  onClick={() => navigate("/overview/fds")}
                >
                  Manage
                </button>
              </div>

              <div className="ov-dir-body">
                <div>
                  <div className="ov-block-head">
                    <b>By business</b>
                    <span>share of directors</span>
                  </div>
                  <div className="ov-dir-rows">
                    {directory.fds.length === 0 && (
                      <div className="ov-dir-sub">
                        {fdBook ? "No funeral directors yet." : "Loading…"}
                      </div>
                    )}
                    {directory.fds.map((b) => (
                      <div key={b.business_id} className={dirSk(fdsLoading, "ov-dir-row")}>
                        <div className="ov-split-row">
                          <span className="ov-split-name">
                            <span className="ov-swatch" style={{ background: b.color }} />
                            {b.name}
                          </span>
                          <span className="ov-split-value ov-num">
                            <Num v={count(b.total)} p={pf} /> ·{" "}
                            <Num v={`${b.share}%`} p={pf} />
                          </span>
                        </div>
                        <div className="ov-track">
                          <span style={{ width: `${b.share * pf}%`, background: b.color }} />
                        </div>
                        <div className="ov-dir-sub">
                          <Num v={count(b.total - b.shared)} p={pf} /> private ·{" "}
                          <Num v={count(b.shared)} p={pf} /> global
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OverviewPage;
